import { useIsOfflineConnectivity } from '@/hooks/desktop/useIsOfflineConnectivity';

import type { SyncBadgeStatus } from '../sync-status-badge.types';

/**
 * The three statuses that mean "a request is in flight right now", which the
 * badge config renders as one spinning `LoaderCircle` + "Syncing…".
 *
 * Every one of them is a claim about work happening over the network, so none
 * of them can be true while the app is offline.
 */
const IN_FLIGHT_SYNC_STATUSES: ReadonlySet<SyncBadgeStatus> = new Set<SyncBadgeStatus>([
  'creating_session',
  'uploading_audio',
  'processing',
]);

/**
 * Reads a sync status the way a clinician who has just lost their connection
 * would: an in-flight status while the app is offline becomes `pending_sync`.
 *
 * The durable status is written by Rust when the drain *starts* a step and
 * rewritten when it finishes or fails, so a link drop in between leaves the row
 * saying `uploading_audio` until the HTTP request gives up. Nothing is uploading;
 * the row is describing an attempt the network already ended.
 *
 * The status itself stays honest about what Rust last did — what is stale is the
 * inference the badge draws from it, so the correction belongs here rather than
 * in the durable state. `pending_sync` is the value the boot reset already maps
 * every in-flight row back to, so this reuses an existing meaning rather than
 * inventing a display-only one.
 *
 * Reads {@link useIsOfflineConnectivity}, which carries its own flag and
 * `isTauri()` gating and whose browser fast-path cannot override a confirmed
 * native `online_ready_to_sync` — so a WebView2 false-offline on Windows cannot
 * make a genuinely-syncing row look parked.
 *
 * That is deliberately the *same rule* as the connectivity banner, not the same
 * code: `ConnectivityAlert` inlines its own copy of it. They agree on both cases
 * that reach a user — confirmed-online silences both, confirmed-offline shows
 * both — but the hook also honours the non-prod `__heidiOfflineTest` toggle,
 * which the banner does not read. So on a dev build with that toggle set the
 * badge downgrades while the banner stays silent. Two copies of one rule will
 * drift again; the durable fix is to give the banner this hook.
 *
 * Failure and terminal statuses pass through untouched: a `sync_failed` row is
 * still a failure whether or not the link is down.
 */
export function useOfflineAwareSyncStatus(
  status: SyncBadgeStatus | undefined
): SyncBadgeStatus | undefined {
  const isOffline = useIsOfflineConnectivity();

  if (!status || !isOffline) return status;
  return IN_FLIGHT_SYNC_STATUSES.has(status) ? 'pending_sync' : status;
}
